import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import API from '../utils/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const COP = (n) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(Number(n) || 0);
const num = (v) => parseFloat(v) || 0;

const estadoInfo = {
  borrador:    { bg: '#F3F4F6', color: '#6B7280', label: '📝 Borrador' },
  enviado:     { bg: '#DBEAFE', color: '#1E40AF', label: '📤 Enviado' },
  revisado:    { bg: '#FEF3C7', color: '#92400E', label: '🔍 Revisado' },
  aprobado:    { bg: '#D1FAE5', color: '#065F46', label: '✅ Aprobado' },
  con_novedad: { bg: '#FEE2E2', color: '#991B1B', label: '⚠️ Con novedad' },
};

// ─────────────────────────────────────────
// Campo de moneda táctil
// ─────────────────────────────────────────
function CampoMoneda({ label, icon, hint, valor, onChange, disabled }) {
  const [raw, setRaw] = useState(String(valor || ''));
  const [focused, setFocused] = useState(false);
  useEffect(() => { if (!focused) setRaw(String(valor || '')); }, [valor, focused]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label style={{ fontSize: 11, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {icon} {label} {hint && <span style={{ fontWeight: 400, color: '#9CA3AF', textTransform: 'none' }}>{hint}</span>}
      </label>
      <div style={{ display: 'flex', alignItems: 'center', border: `2px solid ${focused ? '#1a1a2e' : '#E5E7EB'}`, borderRadius: 10, background: disabled ? '#F9FAFB' : 'white', overflow: 'hidden' }}>
        <span style={{ padding: '0 10px', color: '#9CA3AF', fontSize: 15, fontWeight: 700, background: '#F9FAFB', alignSelf: 'stretch', display: 'flex', alignItems: 'center', borderRight: '1px solid #E5E7EB' }}>$</span>
        <input
          type="text" inputMode="numeric"
          value={focused ? raw : (valor > 0 ? COP(valor).replace(/[$\s]/g, '') : '')}
          onChange={(e) => { const v = e.target.value.replace(/[^\d]/g, ''); setRaw(v); onChange(parseFloat(v) || 0); }}
          onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
          disabled={disabled} placeholder="0"
          style={{ flex: 1, padding: '11px 12px', border: 'none', outline: 'none', fontSize: 16, fontWeight: 600, color: '#111827', background: 'transparent', fontFamily: "'Courier New', monospace" }}
        />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────
// Modal: confirmar y bloquear
// ─────────────────────────────────────────
function ModalConfirmar({ totalReal, totalSalidas, guardando, onConfirmar, onCancelar }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
      <div style={{ background: 'white', borderRadius: 16, width: '100%', maxWidth: 380, overflow: 'hidden', boxShadow: '0 24px 64px rgba(0,0,0,0.3)' }}>
        <div style={{ padding: '20px 24px', background: '#1a1a2e', color: 'white', textAlign: 'center' }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>🔒</div>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800 }}>¿Confirmas el cierre del día?</h3>
          <p style={{ margin: '6px 0 0', fontSize: 12, opacity: 0.7 }}>Una vez enviado quedará bloqueado</p>
        </div>
        <div style={{ padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ background: '#F0FDF4', borderRadius: 10, padding: '10px 14px' }}>
            <div style={{ fontSize: 11, color: '#6B7280', fontWeight: 700 }}>💵 EFECTIVO CONTADO</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#10B981', fontFamily: 'monospace' }}>{COP(totalReal)}</div>
          </div>
          <div style={{ background: '#FEF2F2', borderRadius: 10, padding: '10px 14px' }}>
            <div style={{ fontSize: 11, color: '#6B7280', fontWeight: 700 }}>📤 SALIDAS</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#EF4444', fontFamily: 'monospace' }}>{COP(totalSalidas)}</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, padding: '16px 22px', borderTop: '1px solid #F3F4F6' }}>
          <button onClick={onCancelar} style={{ flex: 1, padding: '11px 0', border: '2px solid #E5E7EB', borderRadius: 10, background: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>Revisar de nuevo</button>
          <button onClick={onConfirmar} disabled={guardando} style={{ flex: 1, padding: '11px 0', border: 'none', borderRadius: 10, background: '#1D4ED8', color: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
            {guardando ? '⏳' : '🔒 Confirmar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────
// Modal: solicitar corrección
// ─────────────────────────────────────────
function ModalSolicitud({ cierreId, bodegaId, onClose }) {
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);

  const handleEnviar = async () => {
    if (!motivo.trim()) { toast.error('Describe el motivo'); return; }
    setEnviando(true);
    try {
      await API.post('/correcciones/solicitar', { tipo: 'cierre_caja', registro_id: cierreId, bodega_id: bodegaId, motivo });
      toast.success('Solicitud enviada a administración');
      onClose();
    } catch { toast.error('Error al enviar solicitud'); }
    finally { setEnviando(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
      <div style={{ background: 'white', borderRadius: 16, width: '100%', maxWidth: 400, padding: 24, boxShadow: '0 24px 64px rgba(0,0,0,0.3)' }}>
        <h3 style={{ fontSize: 17, fontWeight: 800, margin: '0 0 8px' }}>✏️ Solicitar corrección</h3>
        <p style={{ fontSize: 13, color: '#6B7280', marginBottom: 14 }}>Explica qué necesitas corregir. Administración recibirá tu solicitud.</p>
        <textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Describe el error..." rows={4}
          style={{ width: '100%', padding: '10px 14px', border: '2px solid #E5E7EB', borderRadius: 10, fontSize: 14, resize: 'none', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box', marginBottom: 14 }} />
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: '11px 0', border: '2px solid #E5E7EB', borderRadius: 10, background: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 700, color: '#374151' }}>Cancelar</button>
          <button onClick={handleEnviar} disabled={enviando || !motivo.trim()} style={{ flex: 1, padding: '11px 0', border: 'none', borderRadius: 10, background: '#F59E0B', color: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
            {enviando ? 'Enviando...' : '📤 Enviar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────
// PANTALLA — /caja
// ─────────────────────────────────────────
const FORM_INICIAL = { entradas_efectivo: 0, usd_contado: 0, observaciones: '' };

export default function Caja() {
  const { usuario } = useAuth();
  const esAdmin = ['gerencia', 'administracion'].includes(usuario?.rol);

  const [bodegas, setBodegas] = useState([]);
  const [bodegaId, setBodegaId] = useState('');
  const [bodegaNombre, setBodegaNombre] = useState('');
  const fecha = format(new Date(), 'yyyy-MM-dd');

  const [cierreId, setCierreId] = useState(null);
  const [estado, setEstado] = useState('borrador');
  const [bloqueado, setBloqueado] = useState(false);
  const [form, setForm] = useState(FORM_INICIAL);
  const [totalReal, setTotalReal] = useState(0);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [modalConfirm, setModalConfirm] = useState(false);
  const [modalCorreccion, setModalCorreccion] = useState(false);

  const [egresos, setEgresos] = useState([]);
  const [formEgreso, setFormEgreso] = useState({ categoria: 'Compras PDV', descripcion: '', nombre_proveedor: '', nit_proveedor: '', monto: '', tiene_factura: false, item_id: '', item_nombre: '', cantidad_item: '' });
  const [guardandoEgreso, setGuardandoEgreso] = useState(false);
  const [itemsInventario, setItemsInventario] = useState([]);
  const [busquedaItem, setBusquedaItem] = useState('');
  const [showItemPicker, setShowItemPicker] = useState(false);
  const fotoRefs = useRef({});

  const soloLectura = bloqueado || (estado === 'aprobado' && !esAdmin);
  const esCentroHistorico = parseInt(bodegaId) === 6;

  // Bodega
  useEffect(() => {
    if (esAdmin) {
      API.get('/bodegas').then((r) => {
        const pvs = r.data.filter((b) => b.tipo === 'punto_venta');
        setBodegas(pvs);
        if (pvs.length) { setBodegaId(String(pvs[0].id)); setBodegaNombre(pvs[0].nombre); }
      }).catch(() => toast.error('No se pudieron cargar los puntos de venta'));
    } else {
      API.get('/app/mi-perfil').then((r) => {
        if (r.data?.bodega_id) { setBodegaId(String(r.data.bodega_id)); setBodegaNombre(r.data.bodega_nombre || ''); }
        else toast.error('No tienes un punto de venta asignado');
      }).catch(() => toast.error('Error cargando tu perfil'));
    }
  }, []);

  const cambiarBodega = (b) => { setBodegaId(String(b.id)); setBodegaNombre(b.nombre); };

  // Cierre del día
  useEffect(() => {
    if (!bodegaId) return;
    setCargando(true);
    API.get(`/caja/cierre/${bodegaId}`, { params: { fecha } })
      .then(({ data }) => {
        if (data.existe) {
          const c = data.cierre;
          setCierreId(c.id); setEstado(c.estado); setBloqueado(c.bloqueado || false); setTotalReal(num(c.total_real));
          setForm({ entradas_efectivo: num(c.entradas_efectivo), usd_contado: num(c.usd_contado), observaciones: c.observaciones || '' });
        } else {
          setCierreId(null); setEstado('borrador'); setBloqueado(false); setTotalReal(0); setForm(FORM_INICIAL);
        }
      })
      .catch(() => toast.error('Error cargando cierre'))
      .finally(() => setCargando(false));
  }, [bodegaId, fecha]);

  // Egresos
  const cargarEgresos = () => {
    if (!bodegaId) return;
    API.get('/caja/egresos', { params: { bodega_id: bodegaId, fecha } }).then((r) => setEgresos(r.data)).catch(() => {});
  };
  useEffect(cargarEgresos, [bodegaId, fecha]);

  useEffect(() => {
    API.get('/caja/inventario-items').then((r) => setItemsInventario(r.data)).catch(() => {});
  }, []);

  const set = (campo) => (val) => setForm((f) => ({ ...f, [campo]: val }));

  const totalSalidas = egresos.reduce((s, e) => s + parseFloat(e.monto || 0), 0);

  const agregarEgreso = async () => {
    if (!formEgreso.monto) { toast.error('El monto es requerido'); return; }
    if (formEgreso.categoria === 'Compras PDV' && !formEgreso.item_id) { toast.error('Selecciona el ítem del inventario'); return; }
    if (formEgreso.categoria !== 'Compras PDV' && !formEgreso.descripcion) { toast.error('La descripción es requerida'); return; }
    setGuardandoEgreso(true);
    try {
      const r = await API.post('/caja/egresos', { ...formEgreso, bodega_id: bodegaId, fecha });
      setEgresos((e) => [...e, r.data]);
      setFormEgreso({ categoria: 'Compras PDV', descripcion: '', nombre_proveedor: '', nit_proveedor: '', monto: '', tiene_factura: false, item_id: '', item_nombre: '', cantidad_item: '' });
      setBusquedaItem('');
      toast.success('Egreso registrado');
    } catch { toast.error('Error al registrar egreso'); }
    finally { setGuardandoEgreso(false); }
  };

  const eliminarEgreso = async (id) => {
    if (!window.confirm('¿Eliminar este egreso?')) return;
    try { await API.delete(`/caja/egresos/${id}`); setEgresos((e) => e.filter((x) => x.id !== id)); }
    catch { toast.error('Error al eliminar'); }
  };

  const subirFotoEgreso = async (e, egresoId) => {
    const file = e.target.files[0]; if (!file) return;
    const fd = new FormData(); fd.append('foto', file);
    try {
      await API.post(`/caja/egresos/${egresoId}/foto`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Foto subida ✅');
      cargarEgresos();
    } catch { toast.error('Error al subir foto'); }
    e.target.value = '';
  };

  const handleGuardar = async (nuevoEstado) => {
    if (!bodegaId) return;
    setGuardando(true);
    try {
      const payload = { ...form, bodega_id: bodegaId, fecha, total_real: totalReal, diferencia: totalReal, estado: nuevoEstado || estado };
      const { data } = await API.post('/caja/cierre', payload);
      if (!cierreId) setCierreId(data.cierre.id);
      setEstado(data.cierre.estado);
      toast.success('💾 Cierre guardado');
    } catch { toast.error('Error al guardar'); }
    finally { setGuardando(false); }
  };

  const handleBloquearYEnviar = async () => {
    setGuardando(true);
    try {
      const payload = { ...form, bodega_id: bodegaId, fecha, total_real: totalReal, diferencia: totalReal, estado: 'enviado' };
      const { data } = await API.post('/caja/cierre', payload);
      const id = data.cierre.id || cierreId;
      await API.post(`/caja/${id}/bloquear`);
      setCierreId(id); setEstado('enviado'); setBloqueado(true);
      toast.success('✅ Cierre enviado y bloqueado');
    } catch { toast.error('Error al enviar cierre'); }
    finally { setGuardando(false); setModalConfirm(false); }
  };

  const itemsFiltrados = itemsInventario.filter((it) => it.nombre.toLowerCase().includes(busquedaItem.toLowerCase())).slice(0, 15);

  return (
    <div style={{ paddingBottom: !soloLectura && !cargando ? 78 : 0 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#111827' }}>💰 Control de caja</h1>
          <div style={{ fontSize: 13, color: '#6B7280', marginTop: 2, textTransform: 'capitalize' }}>{bodegaNombre} · {format(new Date(fecha + 'T12:00'), "d 'de' MMM", { locale: es })}</div>
        </div>
        <span style={{ padding: '5px 12px', borderRadius: 20, fontWeight: 700, fontSize: 11, whiteSpace: 'nowrap', background: estadoInfo[estado]?.bg, color: estadoInfo[estado]?.color }}>
          {estadoInfo[estado]?.label}
        </span>
      </div>

      {esAdmin && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 14, overflowX: 'auto', paddingBottom: 4 }}>
          {bodegas.map((b) => (
            <button key={b.id} onClick={() => cambiarBodega(b)} style={{ padding: '6px 14px', borderRadius: 20, cursor: 'pointer', fontSize: 12, fontWeight: 700, border: `2px solid ${String(bodegaId) === String(b.id) ? '#1a1a2e' : '#E5E7EB'}`, background: String(bodegaId) === String(b.id) ? '#1a1a2e' : 'white', color: String(bodegaId) === String(b.id) ? 'white' : '#374151', whiteSpace: 'nowrap', flexShrink: 0 }}>{b.nombre}</button>
          ))}
        </div>
      )}

      {cargando ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#9CA3AF' }}>Cargando...</div>
      ) : (
        <>
          {bloqueado && !esAdmin && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: 12, padding: '12px 16px', marginBottom: 14, fontSize: 13, color: '#92400E', fontWeight: 600 }}>
              <span>🔒 Cierre bloqueado y enviado</span>
              <button onClick={() => setModalCorreccion(true)} style={{ padding: '6px 12px', border: '1px solid #F59E0B', borderRadius: 8, background: 'white', color: '#92400E', cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>✏️ Solicitar corrección</button>
            </div>
          )}

          {/* Entradas */}
          <div style={{ background: 'white', borderRadius: 14, padding: 14, marginBottom: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.07)', borderTop: '3px solid #10B981' }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: '#111827', marginBottom: 10 }}>💰 Entradas de efectivo</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <CampoMoneda label="Entradas de efectivo" icon="💵" hint="(menudo, adelantos)" valor={form.entradas_efectivo} onChange={set('entradas_efectivo')} disabled={soloLectura} />
              {esCentroHistorico && (
                <CampoMoneda label="Dólares contados (USD)" icon="🇺🇸" hint="(en USD)" valor={form.usd_contado} onChange={set('usd_contado')} disabled={soloLectura} />
              )}
            </div>
          </div>

          {/* Egresos */}
          <div style={{ background: 'white', borderRadius: 14, padding: 14, marginBottom: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.07)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span style={{ fontWeight: 800, fontSize: 13, color: '#111827' }}>💸 Egresos del día</span>
              <span style={{ fontFamily: 'monospace', fontWeight: 800, color: '#EF4444', fontSize: 13 }}>{COP(totalSalidas)}</span>
            </div>

            {!soloLectura && (
              <div style={{ background: '#FEF2F2', borderRadius: 10, padding: 12, marginBottom: 12, border: '1px solid #FECACA' }}>
                <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                  {['Compras PDV', 'Otros'].map((cat) => (
                    <button key={cat} onClick={() => setFormEgreso((f) => ({ ...f, categoria: cat, item_id: '', item_nombre: '', cantidad_item: '', descripcion: '' }))}
                      style={{ flex: 1, padding: '7px 0', border: `2px solid ${formEgreso.categoria === cat ? '#EF4444' : '#E5E7EB'}`, borderRadius: 8, background: formEgreso.categoria === cat ? '#EF4444' : 'white', color: formEgreso.categoria === cat ? 'white' : '#374151', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                      {cat}
                    </button>
                  ))}
                </div>

                {formEgreso.categoria === 'Compras PDV' ? (
                  <div style={{ marginBottom: 10, position: 'relative' }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', marginBottom: 4 }}>Ítem del inventario *</div>
                    <input value={busquedaItem} onChange={(e) => { setBusquedaItem(e.target.value); setShowItemPicker(true); }} onFocus={() => setShowItemPicker(true)}
                      placeholder="Buscar ítem..." style={{ width: '100%', padding: '9px 10px', border: `2px solid ${formEgreso.item_id ? '#059669' : '#E5E7EB'}`, borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                    {formEgreso.item_id && (
                      <div style={{ marginTop: 4, fontSize: 12, color: '#059669', fontWeight: 700 }}>
                        ✅ {formEgreso.item_nombre}
                        <button onClick={() => { setFormEgreso((f) => ({ ...f, item_id: '', item_nombre: '' })); setBusquedaItem(''); }} style={{ marginLeft: 8, background: 'none', border: 'none', color: '#9CA3AF', cursor: 'pointer', fontSize: 12 }}>✕</button>
                      </div>
                    )}
                    {showItemPicker && busquedaItem.length > 0 && (
                      <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '2px solid #E5E7EB', borderRadius: 8, zIndex: 50, maxHeight: 200, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', marginTop: 2 }}>
                        {itemsFiltrados.length === 0 ? (
                          <div style={{ padding: '12px 14px', color: '#9CA3AF', fontSize: 13 }}>Sin resultados</div>
                        ) : itemsFiltrados.map((it) => (
                          <div key={it.id} onClick={() => { setFormEgreso((f) => ({ ...f, item_id: it.id, item_nombre: it.nombre })); setBusquedaItem(it.nombre); setShowItemPicker(false); }}
                            style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #F3F4F6', fontSize: 13 }}>
                            <span style={{ fontWeight: 600 }}>{it.nombre}</span>
                            <span style={{ fontSize: 11, color: '#9CA3AF', marginLeft: 8 }}>{it.categoria}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', marginBottom: 4 }}>Descripción *</div>
                    <input value={formEgreso.descripcion} onChange={(e) => setFormEgreso((f) => ({ ...f, descripcion: e.target.value }))} placeholder="¿Qué se pagó?"
                      style={{ width: '100%', padding: '9px 10px', border: '2px solid #E5E7EB', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                  </div>
                )}

                {formEgreso.categoria === 'Compras PDV' && (
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', marginBottom: 4 }}>Cantidad *</div>
                    <input type="number" inputMode="decimal" value={formEgreso.cantidad_item} onChange={(e) => setFormEgreso((f) => ({ ...f, cantidad_item: e.target.value }))} placeholder="0"
                      style={{ width: '100%', padding: '9px 10px', border: '2px solid #E5E7EB', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', marginBottom: 4 }}>Proveedor</div>
                    <input value={formEgreso.nombre_proveedor} onChange={(e) => setFormEgreso((f) => ({ ...f, nombre_proveedor: e.target.value }))} placeholder="Nombre"
                      style={{ width: '100%', padding: '9px 10px', border: '2px solid #E5E7EB', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', marginBottom: 4 }}>NIT / Cédula</div>
                    <input value={formEgreso.nit_proveedor} onChange={(e) => setFormEgreso((f) => ({ ...f, nit_proveedor: e.target.value }))} placeholder="Opcional"
                      style={{ width: '100%', padding: '9px 10px', border: '2px solid #E5E7EB', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                  </div>
                </div>

                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', marginBottom: 4 }}>Monto *</div>
                  <input type="number" inputMode="decimal" value={formEgreso.monto} onChange={(e) => setFormEgreso((f) => ({ ...f, monto: e.target.value }))} placeholder="0"
                    style={{ width: '100%', padding: '9px 10px', border: '2px solid #E5E7EB', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box', fontWeight: 700 }} />
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                    <input type="checkbox" checked={formEgreso.tiene_factura} onChange={(e) => setFormEgreso((f) => ({ ...f, tiene_factura: e.target.checked }))} />
                    ¿Tiene factura?
                  </label>
                  <button onClick={agregarEgreso} disabled={guardandoEgreso} style={{ padding: '9px 18px', background: '#EF4444', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
                    {guardandoEgreso ? '⏳' : '+ Agregar'}
                  </button>
                </div>
              </div>
            )}

            {egresos.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#9CA3AF', fontSize: 13, padding: '10px 0' }}>Sin egresos registrados hoy</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {egresos.map((eg) => (
                  <div key={eg.id} style={{ padding: '10px 12px', background: '#FAFAFA', borderRadius: 10, border: '1px solid #F3F4F6' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 12, color: '#374151' }}>
                          <span style={{ background: '#FEE2E2', color: '#991B1B', padding: '1px 6px', borderRadius: 5, fontSize: 10, marginRight: 6 }}>{eg.categoria}</span>
                          {eg.descripcion}
                        </div>
                        {eg.nombre_proveedor && <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>🏪 {eg.nombre_proveedor}</div>}
                      </div>
                      <div style={{ fontFamily: 'monospace', fontWeight: 800, color: '#EF4444', fontSize: 14, whiteSpace: 'nowrap' }}>{COP(eg.monto)}</div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
                      <span style={{ fontSize: 11, color: eg.tiene_factura ? '#059669' : '#F59E0B' }}>{eg.tiene_factura ? '✅ Con factura' : '⚠️ Sin factura'}</span>
                      {!soloLectura && (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <input ref={(el) => (fotoRefs.current[eg.id] = el)} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={(e) => subirFotoEgreso(e, eg.id)} />
                          <button onClick={() => fotoRefs.current[eg.id]?.click()} style={{ padding: '4px 8px', border: '1px solid #E5E7EB', borderRadius: 6, background: 'white', cursor: 'pointer', fontSize: 12 }}>📷</button>
                          <button onClick={() => eliminarEgreso(eg.id)} style={{ padding: '4px 8px', border: '1px solid #FECACA', borderRadius: 6, background: 'white', cursor: 'pointer', fontSize: 12, color: '#EF4444' }}>🗑️</button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Resumen */}
          <div style={{ background: 'white', borderRadius: 14, marginBottom: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden', border: '2px solid #F3F4F6' }}>
            <div style={{ padding: '12px 16px', background: '#1a1a2e', color: 'white', fontSize: 13, fontWeight: 800 }}>📊 Resumen del día</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, padding: 14 }}>
              <div style={{ background: '#F9FAFB', borderRadius: 10, padding: '10px 12px' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase' }}>💰 Entradas</div>
                <div style={{ fontSize: 16, fontWeight: 800, color: '#10B981', fontFamily: 'monospace' }}>{COP(form.entradas_efectivo)}</div>
              </div>
              <div style={{ background: '#F9FAFB', borderRadius: 10, padding: '10px 12px' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase' }}>📤 Salidas</div>
                <div style={{ fontSize: 16, fontWeight: 800, color: '#EF4444', fontFamily: 'monospace' }}>{COP(totalSalidas)}</div>
              </div>
            </div>
            <div style={{ padding: '12px 16px 16px', background: '#F9FAFB', borderTop: '1px solid #F3F4F6' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', marginBottom: 8 }}>Efectivo real contado</div>
              <div style={{ display: 'flex', alignItems: 'center', border: '2px solid #1a1a2e', borderRadius: 10, background: 'white', overflow: 'hidden' }}>
                <span style={{ padding: '0 10px', color: '#9CA3AF', fontSize: 16, fontWeight: 700 }}>$</span>
                <input type="text" inputMode="numeric" value={totalReal || ''} disabled={soloLectura}
                  onChange={(e) => setTotalReal(parseFloat(e.target.value.replace(/[^\d]/g, '')) || 0)}
                  style={{ flex: 1, padding: '12px 12px 12px 0', border: 'none', outline: 'none', fontSize: 18, fontWeight: 700, color: '#111827', background: 'transparent', fontFamily: "'Courier New', monospace" }} placeholder="0" />
              </div>
            </div>
          </div>

          {/* Observaciones */}
          <div style={{ background: 'white', borderRadius: 14, padding: 14, marginBottom: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.07)' }}>
            <label style={{ fontSize: 12, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 8 }}>📝 Observaciones</label>
            <textarea value={form.observaciones} onChange={(e) => setForm((f) => ({ ...f, observaciones: e.target.value }))} disabled={soloLectura}
              placeholder="Novedades del día..." rows={3}
              style={{ width: '100%', padding: '10px 12px', border: '2px solid #E5E7EB', borderRadius: 10, fontSize: 14, resize: 'vertical', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }} />
          </div>
        </>
      )}

      {/* Barra de acción */}
      {!cargando && !soloLectura && (
        <div style={{ position: 'fixed', left: 0, right: 0, bottom: 'calc(70px + env(safe-area-inset-bottom, 0px))', display: 'flex', gap: 10, padding: '10px 20px', background: 'white', borderTop: '1px solid #E5E7EB', boxShadow: '0 -2px 10px rgba(0,0,0,0.06)', zIndex: 90 }}>
          <button onClick={() => handleGuardar('borrador')} disabled={guardando}
            style={{ flex: 1, padding: '13px 0', border: '2px solid #E5E7EB', borderRadius: 12, background: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 700, color: '#374151' }}>
            {guardando ? '⏳' : '💾 Borrador'}
          </button>
          {(estado === 'borrador' || estado === 'con_novedad') && (
            <button onClick={() => setModalConfirm(true)} disabled={guardando}
              style={{ flex: 2, padding: '13px 0', border: 'none', borderRadius: 12, background: '#1D4ED8', color: 'white', cursor: 'pointer', fontSize: 14, fontWeight: 700 }}>
              📤 Enviar y cerrar
            </button>
          )}
        </div>
      )}

      {modalConfirm && (
        <ModalConfirmar totalReal={totalReal} totalSalidas={totalSalidas} guardando={guardando} onConfirmar={handleBloquearYEnviar} onCancelar={() => setModalConfirm(false)} />
      )}
      {modalCorreccion && (
        <ModalSolicitud cierreId={cierreId} bodegaId={bodegaId} onClose={() => setModalCorreccion(false)} />
      )}
    </div>
  );
}
